
import { Link, useLocation } from "react-router-dom";
import { Home, Calendar, Dumbbell, ClipboardList, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMobileMenu } from "@/components/dashboard/header/useMobileMenu";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { getDashboardNavItems, useStudentNavItems } from "./navigation-items";

interface MobileNavItem {
  title: string;
  icon: React.ElementType;
  href: string;
  iconClassName?: string;
  bgClassName?: string;
}

interface MobileNavProps {
  items?: MobileNavItem[];
  dashboardType: "student" | "trainer";
}

export const MobileNavBar = ({ items: propItems, dashboardType }: MobileNavProps) => {
  const location = useLocation();
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();
  const { toggleMenu, isMenuOpen, closeMenu } = useMobileMenu();

  // Get navigation items based on dashboard type
  const studentNavItems = useStudentNavItems(language as 'en' | 'pt');
  const trainerNavItems = getDashboardNavItems(language as 'en' | 'pt');
  
  // Use provided items or generate language-specific items
  const items = propItems || (
    dashboardType === "student" 
      ? studentNavItems
      : trainerNavItems
  );

  const baseUrl = getPath(dashboardType === "student" ? "/student-dashboard" : "/dashboard");

  const handleToggleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleMenu();
  };

  const handleNavClick = () => {
    closeMenu();
  };

  // Helper function to check if a path is active, considering language prefixes
  const isPathActive = (href: string) => {
    // Strip language prefix from current location for comparison
    const currentPath = location.pathname.replace(/^\/pt/, '');
    const pathWithoutPrefix = href.replace(/^\/pt/, '');
    
    return currentPath === pathWithoutPrefix || 
           (pathWithoutPrefix !== baseUrl.replace(/^\/pt/, '') && 
            currentPath.startsWith(pathWithoutPrefix));
  };

  return (
    <nav className="fixed bottom-4 left-0 right-0 z-50 mx-auto w-[90%] max-w-xs px-4 block md:hidden animate-fade-in">
      <div className="flex justify-around items-center h-16 bg-white/80 backdrop-blur-xl rounded-full shadow-xl border border-slate-100/50">
        <Button 
          onClick={handleToggleClick}
          variant="ghost"
          className="flex items-center justify-center w-10 p-0 MobileMenuToggle"
          aria-label="Abrir menu"
          type="button"
          aria-controls="mobile-menu"
          aria-expanded={isMenuOpen}
        >
          <div className="flex items-center justify-center rounded-full p-2.5 text-flame hover:bg-flame/10 transition-colors">
            {isMenuOpen ? (
              <X className="h-5 w-5 stroke-[2.5]" />
            ) : (
              <Menu className="h-5 w-5 stroke-[2.5]" />
            )}
          </div>
        </Button>
        
        {items.map((item) => {
          const isActive = isPathActive(item.href);
          return (
            <Link
              key={item.href}
              to={item.href}
              className="flex items-center justify-center w-1/5"
              onClick={handleNavClick}
            >
              <div
                className={cn(
                  "flex items-center justify-center rounded-full p-2.5 transition-colors",
                  isActive 
                    ? "bg-flame/10 text-flame shadow-sm" 
                    : "text-slate-400 hover:text-flame hover:bg-flame/5"
                )}
              >
                <item.icon className="h-5 w-5 stroke-[2.5]" />
              </div>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};
